import React from 'react';
import { observable, reaction } from 'mobx';
import { OCI_COMPARTMENT_SUMMARY_ENTITIES, OCI_ENTITY_TYPES } from '@kentik/ui-shared/hybrid/constants';
import { memoize, orderBy, get } from 'lodash';
import { inject, observer } from 'mobx-react';
import styled from 'styled-components';
import {
  Card,
  Button,
  Flex,
  Popover,
  Icon,
  Tag,
  Text,
  Spinner,
  Box,
  Heading,
  Checkbox,
  Suspense,
  Tooltip
} from 'core/components';
import OciLogo from 'app/components/OracleLogo';
import { Field, Select, RemoveButton, Form } from 'core/form';
import { Filter, VirtualizedTable } from 'core/components/table';
import CloudIcon from 'app/views/hybrid/maps/components/CloudIcon';
import TimedCallout from 'app/views/hybrid/maps/components/TimedCallout';
import CompartmentFilterCollection from 'app/stores/clouds/oci/CompartmentFilterCollection';

import MapSearchGroupDialog from './components/MapSearchGroupDialog';

const OCI_COMPARTMENTS_GROUPS_MODULE_GROUP = 'ocicompartmentgroups';

const StyledBox = styled(Box)`
  .flex-grow-1 {
    flex-grow: 1;
  }
`;

const initialFields = {
  subscriptions: {},
  name: {
    label: 'Name / ID'
  },
  view: {
    label: 'View',
    options: [
      { value: 'all', label: 'All' },
      { value: 'selected', label: 'Selected' }
    ],
    defaultValue: 'all'
  },
  group: {
    label: 'Group',
    options: [{ value: '', label: '---' }],
    defaultValue: ''
  }
};
@Form({ fields: initialFields, options: { name: 'aws-accounts-filter' } })
@inject('$clouds', '$hybridMap', '$auth', '$moduleConfig')
@observer
export default class MapSearchAwsSubscriptionsPanel extends React.Component {
  ociCloudMapCollectionDisposer = () => {};

  @observable
  ociCompartmentCollection = new CompartmentFilterCollection([]);

  tempSelectedGroup = '';

  state = {
    popoverOpen: false,
    calloutMessageArr: [],
    displayGroupDialog: false,
    isManagingCompartmentGroup: false
  };

  initializeOciCompartmentCollection() {
    const { $hybridMap, form } = this.props;
    const { ociCloudMapCollection } = $hybridMap;

    const ociCompartments = ociCloudMapCollection.ociCompartmentEntities;

    const compartmentSummary = ociCloudMapCollection.Summary?.Compartments ?? {};

    const defaultSummary = OCI_COMPARTMENT_SUMMARY_ENTITIES.reduce((carry, entityType) => {
      carry[entityType] = 0;
      return carry;
    }, {});

    // set a summary for each account model
    ociCompartments.forEach((ociCompartment) => {
      ociCompartment.summary = compartmentSummary[ociCompartment.id] ?? defaultSummary;
    });

    const sortedModels = orderBy(
      ociCompartments,
      (model) => Object.values(get(model, `summary.${OCI_ENTITY_TYPES.VIRTUAL_CLOUD_NETWORK}`, {})).length,
      'desc'
    );

    this.ociCompartmentCollection.set(sortedModels);

    const selectedOciCompartmentsInUserOptions = $hybridMap.selectedOciCompartments;

    if (selectedOciCompartmentsInUserOptions.length > 0) {
      this.ociCompartmentCollection.unfiltered.forEach((ociCompartmentModel) => {
        if (selectedOciCompartmentsInUserOptions.includes(ociCompartmentModel.get('id'))) {
          ociCompartmentModel.select({ multi: true });
        }
      });
    } else {
      this.ociCompartmentCollection.selectAll({ multi: true });
    }

    this.ociCompartmentCollection.applyPagination();

    // autoselect group and filter to show only accounts that apply to this group
    if ($hybridMap.selectedOciCompartmentGroup) {
      form.setValue('group', $hybridMap.selectedOciCompartmentGroup);
      this.onGroupDropdownChange({ value: $hybridMap.selectedOciCompartmentGroup });
    }
  }

  async fetchModuleConfig() {
    const { $moduleConfig } = this.props;

    return $moduleConfig.fetchModuleConfig(OCI_COMPARTMENTS_GROUPS_MODULE_GROUP);
  }

  componentDidMount() {
    const { $hybridMap } = this.props;

    const promiseFunction = async () => {
      await this.fetchModuleConfig();

      this.initializeOciCompartmentCollection();
    };

    promiseFunction();

    this.ociCloudMapCollectionDisposer = reaction(
      () => $hybridMap.ociCloudMapCollection.ociCompartmentEntities.length,
      () => this.initializeOciCompartmentCollection()
    );
  }

  componentWillUnmount() {
    // execute to avoid memory leak
    this.ociCloudMapCollectionDisposer();
  }

  onPopoverClose = () => {
    const { onChange, $auth, form } = this.props;

    // do nothing when topology is loading
    if (this.isLoading) {
      return null;
    }

    this.setState({ popoverOpen: false }, () => {
      // do not persist in demo account
      onChange({
        persist: !$auth.isDemoUser,
        ociCompartmentIds: this.ociCompartmentCollection.selectedCompartmentIds,
        selectedGroup: form.getValue('group')
      });
    });

    return null;
  };

  onGroupDropdownChange = ({ value }) => {
    this.tempSelectedGroup = value;

    if (value !== '' && this.companyOciCompartmentGroups[value]) {
      this.ociCompartmentCollection.addGroupFilter(this.companyOciCompartmentGroups[value]);
    }

    if (value === '') {
      // reset selections back to the initial one
      this.ociCompartmentCollection.removeGroupFilter();
      this.ociCompartmentCollection.applyPagination();
    }
  };

  changePage = (page) => {
    this.ociCompartmentCollection.setPage(page);
    this.ociCompartmentCollection.applyPageChangeFilter();
  };

  onViewDropdownChange = ({ value }) => {
    if (value === 'selected') {
      this.ociCompartmentCollection.showSelectedCompartments();
      return;
    }

    if (value === 'all') {
      this.ociCompartmentCollection.showAllCompartments();
      return;
    }

    console.warn('Unhandled inViewDropdownChange', value);
  };

  get columns() {
    const columns = [
      {
        key: 'id',
        label: 'Selected',
        width: 50,
        renderer: ({ model }) => (
          <Checkbox mt="2px" checked={model.isSelected} onChange={() => model.select({ multi: true })} />
        )
      },
      {
        key: 'name',
        label: 'Name',
        name: 'name',
        className: 'flex-grow-1',
        flexGrow: 1,
        width: '450px',
        renderer: ({ model }) => (
          <Flex flexDirection="column" gap={1}>
            <Box>
              <Text as="b">{model.get('name')}</Text>
            </Box>
            {model.get('name') !== model.get('id') && (
              <Box whiteSpace="nowrap" textOverflow="ellipsis" style={{ overflow: 'hidden' }}>
                <Text muted small>
                  {model.get('id')}
                </Text>
              </Box>
            )}
          </Flex>
        )
      },
      {
        key: 'summary',
        label: 'Summary',
        name: 'summary',
        renderer: ({ model }) => {
          const summary = model.get('summary');

          if (!summary) {
            return null;
          }

          return (
            <Flex gap={1} flexWrap="wrap" justifyContent="space-evenly">
              {OCI_COMPARTMENT_SUMMARY_ENTITIES.map((entityType) => (
                <Tooltip key={entityType} content={entityType}>
                  <Flex gap={1} flexDirection="column" alignItems="center">
                    <CloudIcon cloudProvider="oci" entity={entityType} iconSize={20} />
                    <Text>{summary[entityType]?.length ?? 0}</Text>
                  </Flex>
                </Tooltip>
              ))}
            </Flex>
          );
        }
      }
    ];

    return columns;
  }

  get companyOciCompartmentGroups() {
    const { $moduleConfig } = this.props;
    return $moduleConfig.get(OCI_COMPARTMENTS_GROUPS_MODULE_GROUP, '', {});
  }

  cancelManageGroup = () => {
    this.ociCompartmentCollection.addGroupFilter(this.companyOciCompartmentGroups[this.tempSelectedGroup]);
    this.tempSelectedGroup = null;

    this.setState({ isManagingCompartmentGroup: false });
  };

  saveGroup = async () => {
    const { $moduleConfig } = this.props;

    return $moduleConfig
      .saveModuleConfig({
        module: OCI_COMPARTMENTS_GROUPS_MODULE_GROUP,
        settings: {
          ...this.companyOciCompartmentGroups,
          [this.tempSelectedGroup]: this.ociCompartmentCollection.selectedCompartmentIds
        }
      })
      .finally(() => {
        this.setState({
          displayGroupDialog: false,
          isManagingCompartmentGroup: false,
          calloutMessageArr: [`${this.tempSelectedGroup} group has been saved.`]
        });

        this.tempSelectedGroup = null;
      });
  };

  createGroup = async (groupName) => {
    const { $moduleConfig } = this.props;

    const message = this.tempSelectedGroup
      ? `${this.tempSelectedGroup} group has been renamed into ${groupName}.`
      : `Group ${groupName} was successfully created.`;

    return $moduleConfig
      .saveModuleConfig({
        module: OCI_COMPARTMENTS_GROUPS_MODULE_GROUP,
        settings: {
          ...this.companyOciCompartmentGroups,
          [groupName]: this.tempSelectedGroup
            ? this.companyOciCompartmentGroups[this.tempSelectedGroup]
            : this.ociCompartmentCollection.selectedCompartmentIds
        }
      })
      .finally(() => {
        this.setState({
          displayGroupDialog: false,
          calloutMessageArr: [message]
        });

        // if group was renamed
        if (this.tempSelectedGroup) {
          this.removeGroup(this.tempSelectedGroup, true);
          this.onGroupDropdownChange({ value: groupName });
          this.tempSelectedGroup = null;
        }
      });
  };

  removeGroup = async (groupNameToRemove = '', ignoreCalloutMessage = false) => {
    const { $moduleConfig, form } = this.props;

    const field = form.getField('group');
    const groupName = groupNameToRemove || field.getValue('group');

    field.setValue('');

    const companyAccountGroups = { ...this.companyOciCompartmentGroups };
    delete companyAccountGroups[groupName];

    this.onGroupDropdownChange({ value: '' });

    const newState = {
      displayGroupDialog: false
    };
    if (!ignoreCalloutMessage) {
      Object.assign(newState, { calloutMessageArr: [`Group ${groupName} was successfully removed.`] });
    }

    return $moduleConfig
      .saveModuleConfig({
        module: OCI_COMPARTMENTS_GROUPS_MODULE_GROUP,
        settings: { ...companyAccountGroups }
      })
      .finally(() => {
        this.setState(newState);
      });
  };

  getOciCompartmentGroupOptions = memoize(
    () => {
      // append stored groups into form dropdown
      const { group } = initialFields;

      const awsAccountGroups = Object.keys(this.companyOciCompartmentGroups).map((awsGroup) => ({
        value: awsGroup,
        label: awsGroup
      }));

      return [...group.options, ...awsAccountGroups];
    },
    () => this.companyOciCompartmentGroups
  );

  onManageGroupClicked = () => {
    const { form } = this.props;

    const selectedGroup = form.getValue('group');
    this.tempSelectedGroup = selectedGroup;

    this.ociCompartmentCollection.resetDiscreteFilters();

    this.setState({
      isManagingCompartmentGroup: true
    });
  };

  render() {
    const { $hybridMap, form } = this.props;
    const { popoverOpen, displayGroupDialog, isManagingCompartmentGroup, calloutMessageArr } = this.state;
    const { pageCount, currentPage } = this.ociCompartmentCollection?.pageState || {};

    return (
      <Popover
        autoFocus={false}
        enforceFocus={false}
        isOpen={popoverOpen}
        onClose={this.onPopoverClose}
        placement="bottom-start"
        content={
          <Suspense
            loading={$hybridMap.ociCloudMapCollection.loading}
            fallback={
              <Flex p={2} alignItems="Center" justifyContent="center" width="100%" gap={1}>
                <Spinner size={24} />
                <Text>Loading OCI Compartments.</Text>
              </Flex>
            }
          >
            <Card display="flex" flexDirection="column" width="750px">
              <Heading p={2} bg="subnavBackground" level={5} mb={0}>
                Compartments Filter
              </Heading>
              <Flex p={1} gap={1} alignItems="center">
                <Field name="name" width="100%" placeholder="Search Compartments...">
                  <Filter collection={this.ociCompartmentCollection} />
                </Field>

                <Field
                  name="view"
                  onQuery={null}
                  onChange={this.onViewDropdownChange}
                  disabled={isManagingCompartmentGroup}
                >
                  <Select small menuWidth={100} />
                </Field>
                <Field
                  name="group"
                  disabled={isManagingCompartmentGroup}
                  options={this.getOciCompartmentGroupOptions()}
                  onChange={this.onGroupDropdownChange}
                >
                  <Select small menuWidth={175} />
                </Field>
              </Flex>
              <Flex px={1} paddingTop="8px" alignItems="center" justifyContent="center" gap={1}>
                <Flex
                  flexGrow="1"
                  width="100%"
                  px={1}
                  justifyContent="space-between"
                  alignItems="center"
                  bg="subnavBackground"
                >
                  {isManagingCompartmentGroup && (
                    <Flex gap="1">
                      <Button small intent="primary" text="Save" onClick={this.saveGroup} />
                      <Button small intent="danger" text="Cancel" onClick={this.cancelManageGroup} />
                    </Flex>
                  )}
                  <Flex justifyContent="center" width="50px" style={{ paddingRight: '6px' }}>
                    <Checkbox
                      checked={!!this.ociCompartmentCollection.isAllSelected}
                      onChange={() => {
                        this.ociCompartmentCollection.selectAll({
                          multi: true
                        });
                      }}
                    />
                  </Flex>
                  <Flex flex="1" py="8px" px={1} justifyContent="space-between" alignItems="center">
                    <Text>{`${this.ociCompartmentCollection.selectedSize} / ${this.ociCompartmentCollection.unfilteredSize} selected`}</Text>
                  </Flex>
                  <Flex flex="1">
                    <Flex gap={2} ml="auto">
                      {form.getValue('group') === '' && (
                        <Button
                          small
                          intent="primary"
                          text="Create Group"
                          onClick={() => {
                            this.setState({ displayGroupDialog: true });
                          }}
                        />
                      )}

                      {form.getValue('group') !== '' && !isManagingCompartmentGroup && (
                        <>
                          <Button small text="Manage Group" onClick={this.onManageGroupClicked} />
                          <Button
                            small
                            text="Rename Group"
                            onClick={() => {
                              const selectedGroup = form.getValue('group');
                              this.tempSelectedGroup = selectedGroup;
                              return this.setState({ displayGroupDialog: true });
                            }}
                          />
                          <RemoveButton
                            buttonProps={{ small: true, intent: 'danger', text: 'Remove Group' }}
                            onRemove={this.removeGroup}
                          />
                        </>
                      )}
                    </Flex>
                  </Flex>
                </Flex>
              </Flex>
              <StyledBox p={1} className="flex-grow-1">
                <VirtualizedTable
                  hideHeader
                  isCollapsed
                  multiSelect
                  onRowClick={this.handleRowClick}
                  style={{ height: 300 }}
                  collection={this.ociCompartmentCollection}
                  columns={this.columns}
                />
              </StyledBox>
              {/* Pagination */}
              {pageCount > 1 && (
                <Flex justifyContent="space-between" p={1} alignItems="center">
                  <Button onClick={() => this.changePage(currentPage - 1)} disabled={currentPage <= 1}>
                    Previous
                  </Button>
                  <Box flexGrow="1" textAlign="center">
                    <Text>
                      Page {currentPage} of {pageCount}
                    </Text>
                  </Box>
                  <Button onClick={() => this.changePage(currentPage + 1)} disabled={currentPage >= pageCount}>
                    Next
                  </Button>
                </Flex>
              )}

              {calloutMessageArr.length > 0 && (
                <TimedCallout timeout={5000} intent="success" key={calloutMessageArr.join('-')}>
                  {calloutMessageArr.map((message) => (
                    <Text key={message}>{message}</Text>
                  ))}
                </TimedCallout>
              )}

              <MapSearchGroupDialog
                isOpen={displayGroupDialog}
                selectedCount={this.ociCompartmentCollection.selectedSize}
                groupName={this.tempSelectedGroup}
                onSubmit={(groupName) => {
                  this.createGroup(groupName).then(() => {
                    // select this group in a form
                    form.setValue('group', groupName);
                    this.onGroupDropdownChange({ value: groupName });
                  });
                }}
                onClose={() => this.setState({ displayGroupDialog: false })}
              />
            </Card>
          </Suspense>
        }
      >
        <Flex alignItems="center">
          <Button
            rightIcon="caret-down"
            height="35px"
            active={popoverOpen}
            onClick={() => this.setState((prevState) => ({ popoverOpen: !prevState.popoverOpen }))}
            small
          >
            <Flex alignItems="center" gap={1} px={1} justifyContent="space-evenly" minWidth="135px">
              <Icon color="oci.red" icon={OciLogo} />
              <div>Compartments</div>
              <div>
                {this.ociCompartmentCollection.selectedSize > 0 && (
                  <Tag bg="aws.orange" color="white.10" round small>
                    {this.ociCompartmentCollection.selectedSize}
                  </Tag>
                )}
              </div>
            </Flex>
          </Button>
        </Flex>
      </Popover>
    );
  }
}
